import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import ProductIconsIllustration from './ProductIconsIllustration';
import { mcpInfoCardMessages } from '../../MCP/McpInfoCard/messages';
import {
  MCP_DEVELOPER_DOC_URL as MCP_INFO_LEARN_MORE_URL,
  MCP_TRELLO_DOC_URL,
} from '../../MCP/mcpUrls';
import { useMcpContext } from '../McpContextProvider';

const cardStyle = xcss({
  backgroundColor: 'elevation.surface',
  borderRadius: 'radius.xxlarge',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  padding: 'space.0',
  overflow: 'hidden',
});

const illustrationHideNarrowStyle = xcss({
  display: 'none',
  [media.above.md]: { display: 'revert' },
});

const illustrationBandStyle = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  height: '120px',
  minHeight: '120px',
  borderBottomWidth: 'border.width',
  borderBottomStyle: 'solid',
  borderBottomColor: 'color.border',
  backgroundColor: 'color.background.neutral.subtle',
  paddingInline: 'space.150',
});

const illustrationInnerStyle = xcss({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '0px',
  overflow: 'hidden',
});

const textSectionStyle = xcss({
  paddingBlock: 'space.200',
});

const contentStyle = xcss({
  paddingInline: 'space.200',
  paddingBottom: 'space.200',
});

const buttonRowStyle = xcss({
  marginTop: 'space.0',
  gap: 'space.200',
  flexWrap: 'wrap',
  alignItems: 'center',
});

const McpInfoCard: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const { scopeClassification } = useMcpContext();
  const isTrelloOnly =
    scopeClassification.hasTrello && !scopeClassification.hasAtlassian;

  const learnMoreUrl = scopeClassification.hasTrello
    ? MCP_TRELLO_DOC_URL
    : MCP_INFO_LEARN_MORE_URL;

  const titleMessage = isTrelloOnly
    ? mcpInfoCardMessages.trelloTitle
    : mcpInfoCardMessages.title;
  const bodyMessage = isTrelloOnly
    ? mcpInfoCardMessages.trelloBody
    : mcpInfoCardMessages.body;

  return (
    <Box
      xcss={cardStyle}
      role="region"
      aria-label={formatMessage(mcpInfoCardMessages.carouselRegionAriaLabel)}
      tabIndex={-1}
    >
      <Box xcss={illustrationHideNarrowStyle}>
        <Box xcss={illustrationBandStyle}>
          <Box xcss={illustrationInnerStyle}>
            <ProductIconsIllustration />
          </Box>
        </Box>
      </Box>
      <Box xcss={contentStyle}>
        <Box xcss={textSectionStyle}>
          <Stack space="space.200">
            <Heading size="xsmall">
              <FormattedMessage {...titleMessage} />
            </Heading>
            <Text as="p" size="small" color="color.text">
              <FormattedMessage {...bodyMessage} />
            </Text>
          </Stack>
        </Box>
        <Flex xcss={buttonRowStyle}>
          <Link href={learnMoreUrl} target="_blank" rel="noopener noreferrer">
            <FormattedMessage {...mcpInfoCardMessages.learnMore} />
          </Link>
        </Flex>
      </Box>
    </Box>
  );
};

export default McpInfoCard;
